import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useBudget } from '../../context/BudgetContext';
import { formatCAD } from '../../utils/formatCurrency';

const COLORS = {
  fixed: '#1E3A5F',
  variable: '#F9A825',
  savings: '#2E7D32',
  deficit: '#C62828',
};

function MetricBox({ label, amount, color, sublabel }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', py: 1.5, px: 1 }}>
      <Typography sx={{ fontSize: { xs: 12, md: 11 }, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.35rem' }, fontWeight: 700, color, lineHeight: 1.2 }}>
        {formatCAD(amount)}
      </Typography>
      {sublabel && (
        <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.25 }}>
          {sublabel}
        </Typography>
      )}
    </Box>
  );
}

export default function ThisMonthChart() {
  const { t } = useTranslation();
  const { derived } = useBudget();

  const { monthlyIncome, monthlyFixedExpenses, monthlyVariableExpenses, monthlyTotalExpenses, monthlyNet } = derived;

  const hasIncome = monthlyIncome > 0;
  const fixedPct = hasIncome ? (monthlyFixedExpenses / monthlyIncome) * 100 : 0;
  const variablePct = hasIncome ? (monthlyVariableExpenses / monthlyIncome) * 100 : 0;
  const savingsPct = hasIncome ? Math.max(0, (monthlyNet / monthlyIncome) * 100) : 0;
  const isDeficit = monthlyNet < 0;
  const overflowPct = isDeficit ? Math.min(100, ((monthlyTotalExpenses - monthlyIncome) / monthlyIncome) * 100) : 0;

  return (
    <Box>
      {/* 3 summary metrics */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          border: '1px solid #EEF0F3',
          borderRadius: '10px',
          overflow: 'hidden',
          mb: 2.5,
        }}
      >
        <MetricBox
          label={t('budgetPlanner.charts.monthlyIncome', 'Ingresos mensuales')}
          amount={monthlyIncome}
          color="#1A1A1A"
          sublabel={hasIncome ? `${formatCAD(monthlyIncome * 12)}/año` : undefined}
        />
        <Box sx={{ width: { xs: 'auto', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, bgcolor: '#EEF0F3', my: { xs: 0, sm: 1 }, mx: { xs: 1, sm: 0 } }} />
        <MetricBox
          label={t('budgetPlanner.charts.totalExpenses', 'Gastos totales')}
          amount={monthlyTotalExpenses}
          color="#7D5A00"
          sublabel={hasIncome ? `${Math.round((monthlyTotalExpenses / monthlyIncome) * 100)}% del ingreso` : undefined}
        />
        <Box sx={{ width: { xs: 'auto', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, bgcolor: '#EEF0F3', my: { xs: 0, sm: 1 }, mx: { xs: 1, sm: 0 } }} />
        <MetricBox
          label={isDeficit
            ? t('budgetPlanner.charts.monthlyDeficit', 'Déficit mensual')
            : t('budgetPlanner.charts.monthlySavings', 'Ahorro mensual')}
          amount={Math.abs(monthlyNet)}
          color={isDeficit ? COLORS.deficit : COLORS.savings}
          sublabel={hasIncome
            ? isDeficit
              ? `${Math.round(Math.abs((monthlyNet / monthlyIncome) * 100))}% sobre tu ingreso`
              : `${Math.round(savingsPct)}% de ahorro`
            : undefined}
        />
      </Box>

      {/* 100% horizontal stacked bar */}
      {hasIncome && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              height: 28,
              borderRadius: '6px',
              overflow: 'hidden',
              bgcolor: '#EEF0F3',
            }}
          >
            {fixedPct > 0 && (
              <Box
                sx={{
                  width: `${Math.min(fixedPct, 100)}%`,
                  bgcolor: COLORS.fixed,
                  transition: 'width 0.4s ease',
                  minWidth: fixedPct > 2 ? '2px' : 0,
                }}
              />
            )}
            {variablePct > 0 && (
              <Box
                sx={{
                  width: `${Math.min(variablePct, 100 - fixedPct)}%`,
                  bgcolor: COLORS.variable,
                  transition: 'width 0.4s ease',
                  minWidth: variablePct > 2 ? '2px' : 0,
                }}
              />
            )}
            {savingsPct > 0 && (
              <Box
                sx={{
                  width: `${savingsPct}%`,
                  bgcolor: COLORS.savings,
                  transition: 'width 0.4s ease',
                  minWidth: savingsPct > 2 ? '2px' : 0,
                }}
              />
            )}
            {isDeficit && overflowPct > 0 && (
              <Box
                sx={{
                  width: `${overflowPct}%`,
                  bgcolor: COLORS.deficit,
                  transition: 'width 0.4s ease',
                  minWidth: '2px',
                }}
              />
            )}
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 3 }, mt: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <LegendItem color={COLORS.fixed} label={t('budgetPlanner.charts.fixedExpenses', 'Gastos fijos')} pct={fixedPct} amount={monthlyFixedExpenses} />
            <LegendItem color={COLORS.variable} label={t('budgetPlanner.charts.variableExpenses', 'Gastos variables')} pct={variablePct} amount={monthlyVariableExpenses} />
            {isDeficit ? (
              <LegendItem color={COLORS.deficit} label={t('budgetPlanner.charts.deficit', 'Déficit')} pct={overflowPct} amount={Math.abs(monthlyNet)} />
            ) : (
              <LegendItem color={COLORS.savings} label={t('budgetPlanner.charts.savings', 'Ahorro')} pct={savingsPct} amount={monthlyNet} />
            )}
          </Box>
        </Box>
      )}

      {/* Annual projection note */}
      {hasIncome && monthlyNet > 0 && (
        <Box
          sx={{
            mt: 1,
            py: 1.25,
            px: 2,
            bgcolor: '#F0FFF4',
            border: '1px solid #C6F6D5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.savings }}>
            {t('budgetPlanner.charts.annualProjection',
              'A este ritmo ahorras {{monthly}}/mes = {{annual}} al año',
              { monthly: formatCAD(monthlyNet), annual: formatCAD(monthlyNet * 12) }
            )}
          </Typography>
        </Box>
      )}

      {isDeficit && (
        <Box
          sx={{
            mt: 1,
            py: 1.25,
            px: 2,
            bgcolor: '#FFF5F5',
            border: '1px solid #FED7D7',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.deficit }}>
            {t('budgetPlanner.charts.deficitWarning',
              'Tus gastos superan tus ingresos por {{amount}}/mes. Revisa tus gastos variables.',
              { amount: formatCAD(Math.abs(monthlyNet)) }
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function LegendItem({ color, label, pct, amount }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12, color: '#5F6B7A' }}>
        {label}: {formatCAD(amount)} ({Math.round(pct)}%)
      </Typography>
    </Box>
  );
}
